import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@lib/components/Modal/Modal';
import Form from '@lib/components/ReactHookForm/FormContainer';
import {
  GetUrlParams,
  ORDERING_DIRECTION_ASC,
  ORDERING_DIRECTION_DESC,
} from '@lib/enums/urls';
import formStyles from '@lib/assets/styles/Form.module.scss';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import IconButtonWithDropDown from '@lib/components/IconButtonWithDropDown/IconButtonWithDropDown';
import { NavItemTypeEnum } from '@lib/components/Menu/Menu';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import AscIcon from '@lib/assets/icons/asc_icon.svg?react';
import DescIcon from '@lib/assets/icons/desc_icon.svg?react';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import { FieldItem, Value, Values } from '@lib/interfaces/form';
import { DOCUMENT_FILTER_EMPTY_VALUES } from './utils/useDocumentFilterFields';
import DocumentsFilterActions from './DocumentsFilterActions';
import styles from './DocumentsFilter.module.scss';

type Props = {
  filterCounter: number;
  filterFields: FieldItem[];
  filterParams: { [key: string]: Value };
  onResetFilter: () => void;
  setFilterParams: (v: { [key: string]: Value }) => void;
};

export default function DocumentsFilter(props: Props) {
  const {
    filterCounter,
    filterFields,
    filterParams,
    onResetFilter,
    setFilterParams,
  } = props;
  const { t } = useTranslation();
  const [paginationParams, setPaginationParams] = usePaginationParams();
  const [isFilterOpened, setFilterOpened] = useState(false);
  const openFilter = () => setFilterOpened(true);
  const closeFilter = () => setFilterOpened(false);
  const clearFilter = () => {
    setFilterOpened(false);
    setFilterParams(DOCUMENT_FILTER_EMPTY_VALUES as { [key: string]: Value });
  };

  const { [GetUrlParams.OrderingDirection]: orderingDirection } =
    paginationParams;

  const defaultValues =
    filterParams && Object.keys(filterParams).length > 0
      ? filterParams
      : DOCUMENT_FILTER_EMPTY_VALUES;

  const onSubmit = (values: Values) => {
    setFilterParams(values as { [key: string]: Value });
    setPaginationParams({
      [GetUrlParams.Page]: '1',
    });
    closeFilter();
  };

  const sortingIcon =
    orderingDirection === ORDERING_DIRECTION_ASC ? (
      <AscIcon key="asc" />
    ) : (
      <DescIcon key="desc" />
    );

  const navItems = [
    {
      id: 'newest-first',
      title: t('newest-first'),
      onClick: () =>
        setPaginationParams({
          [GetUrlParams.OrderingDirection]: ORDERING_DIRECTION_DESC,
        }),
      type: NavItemTypeEnum.PrimaryText,
      itemClassName:
        orderingDirection === ORDERING_DIRECTION_DESC ? 'active' : undefined,
    },
    {
      id: 'oldest-first',
      title: t('oldest-first'),
      onClick: () =>
        setPaginationParams({
          [GetUrlParams.OrderingDirection]: ORDERING_DIRECTION_ASC,
        }),
      type: NavItemTypeEnum.PrimaryText,
      itemClassName:
        orderingDirection === ORDERING_DIRECTION_ASC ? 'active' : undefined,
    },
  ];

  return (
    <>
      <div className={styles.filterWrap}>
        <div className={styles.filter}>
          <div className={styles.filterRow}>
            <div>
              <Chip
                counter={filterCounter}
                label={t('filter')}
                leftIcon="filter_alt"
                leftIconSymbolsOutlined
                onClick={openFilter}
                size={ChipSizes.large}
                type={ChipTypes.chipFilter}
                tooltipForCroppedTextDisabled
              />
            </div>
            {!!filterCounter && (
              <div className={styles.clearBtnCol}>
                <IconButton
                  type={IconButtonTypes.TertiaryFilled}
                  icon="cancel"
                  onClick={onResetFilter}
                />
              </div>
            )}
          </div>
          <div className={styles.sortingRow}>
            <IconButtonWithDropDown
              buttonProps={{
                svgIcon: sortingIcon,
                className: styles.sortingBtn,
              }}
              popoverProps={{
                width: 168,
                placement: 'bottomRight',
              }}
              navItems={navItems}
            />
          </div>
        </div>
      </div>
      <Modal
        isOpen={isFilterOpened}
        caption={t('filter')}
        onClose={closeFilter}
      >
        <Form
          fields={filterFields}
          onSubmit={onSubmit}
          defaultValues={defaultValues}
          actionsComponent={DocumentsFilterActions}
          actionsComponentProps={{
            onCancel: clearFilter,
          }}
          formClassName={formStyles.form}
        />
      </Modal>
    </>
  );
}
